/** @jsx jsx */
import { useState, useContext } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Grid, Link as ExternalLink, Box, Text, jsx } from "theme-ui"
import { Link } from "gatsby"
import addToMailchimp from "gatsby-plugin-mailchimp"

import { Context } from "../../context/MainContext"

import Input from "../Forms/Input"
import { SiteCredit } from "../SiteCredit"

import { animatedHover } from "../../mixins"
import { fbTrack } from "../../util"

function Footer({ transparent }) {
  const data = useStaticQuery(graphql`
    query FooterQuery {
      sanityFooter {
        links {
          title
          page {
            slug {
              current
            }
          }
        }
      }
    }
  `)

  const { sanityFooter } = data
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")

  function handleSetMessage(message) {
    setMessage(message)
    const timeout = setTimeout(() => {
      setMessage("")
      clearTimeout(timeout)
    }, 8000)
  }

  function handleEmailSubmission(value) {
    setLoading(true)

    addToMailchimp(value)
      .then(data => {
        handleSetMessage(data.msg)
        if (data.result !== "error") {
          fbTrack("track", "Complete Registration")
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const date = new Date()

  const styles = {
    container: {
      padding: ["40px 8px 50px", "40px 8px 50px", "40px 8px 50px", "50px 20px"],
      gridTemplateColumns: [
        "repeat(6, 1fr)",
        "repeat(6, 1fr)",
        "repeat(6, 1fr)",
        "repeat(12, 1fr)",
      ],
      gridTemplateRows: "auto 1fr",
      gridGap: ["30px 5px", "30px 5px", "30px 5px", 2],
      background: transparent
        ? "transparent"
        : "linear-gradient(-180deg, #EFDEC5 0%, #F7E8A1 100%)",
      color: "secondaryText",
      fontSize: "18px",
      ".links": {
        a: {
          ...animatedHover("#333", 1),
          fontFamily: "secondary",
          fontSize: "18px",
          color: "#333",
        },
      },
    },
    email: {
      display: "flex",
      flexDirection: "column",
      alignItems: ["flex-start", "flex-start", "center", "flex-start"],
      form: {
        width: "100%",
        maxWidth: ["100%", "100%", "570px", "570px"],
      },
      gridColumn: ["1 / -1", "1 / -1", "1 / -1", "1 / 6"],
      gridRow: 1,
    },
    links1: {
      div: {
        marginBottom: "1em",
      },
      gridColumn: ["1 / 4", "1 / 4", "1 / 4", "7 / 9"],
      textAlign: ["left", "left", "center", "left"],
      gridRow: [2, 2, 2, 1],
      display: "flex",
      flexDirection: "column",
    },
    links2: {
      div: {
        marginBottom: "1em",
      },
      gridColumn: ["4 / -1", "4 / -1", "4 / -1", "9 / 11"],
      textAlign: ["left", "left", "center", "left"],
      display: "flex",
      flexDirection: "column",
      gridRow: [2, 2, 2, 1],
    },
    hotness: {
      textAlign: ["center", "center", "center", "left"],
      gridRow: [3, 3, 3, 2],
      gridColumn: ["1 / -1", "1 / -1", "1 / -1", "1 / 6"],
      alignSelf: "end",
    },
    copyright: {
      textAlign: ["center", "center", "center", "left"],
      gridRow: [4, 4, 4, 2],
      gridColumn: ["1 / -1", "1 / -1", "1 / -1", "7 / 9"],
      alignSelf: "end",
    },
    credit: {
      textAlign: ["center", "center", "center", "left"],
      gridRow: [5, 5, 5, 2],
      gridColumn: ["1 / -1", "1 / -1", "1 / -1", "9 /11"],
      alignSelf: "end",
    },
  }

  const {
    store: { isSiteCreditOpen },
    toggleSiteCredit,
  } = useContext(Context)

  return (
    <Grid as="footer" id="footer" sx={styles.container}>
      <Box sx={styles.email}>
        <Text as="p" sx={{ lineHeight: 1.5 }} variant="secondary">
          A LITTLE ADVENTURE IN YOUR INBOX
          <br />
          Sign up for updates, pop-ups, and recipes.
        </Text>

        <Input
          loading={loading}
          handleSubmit={handleEmailSubmission}
          type="email"
          placeholder="Your Email"
        />
        <Text
          as="span"
          variant="secondary"
          sx={{ paddingTop: "0.5em" }}
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </Box>
      <Box className="links" sx={styles.links1}>
        <div>
          <ExternalLink
            title="Shop Marco Merch"
            href="https://www.shop.marcoicecream.com/"
            target="_blank"
            rel="noopener"
          >
            MERCH
          </ExternalLink>
        </div>
        <div>
          <Link to="/find-us" alt="Find Us">
            FIND US
          </Link>
        </div>
        <div>
          <ExternalLink
            href="mailto:hello@marcoicecream.com?subject=Website%20Inquiry"
            alt="Contact"
          >
            CONTACT
          </ExternalLink>
        </div>
        {sanityFooter.links && sanityFooter.links.length > 0
          ? sanityFooter.links.map(link => (
              <div key={link.title}>
                <Link
                  to={link.page.slug.current}
                  title={link.title.toUpperCase()}
                >
                  {link.title.toUpperCase()}
                </Link>
              </div>
            ))
          : null}
      </Box>
      <Box className="links" sx={styles.links2}>
        <div>
          <ExternalLink
            rel="noopener noreferrer"
            target="_blank"
            alt="Instagram"
            href="https://www.instagram.com/marcoicecream/"
          >
            INSTAGRAM
          </ExternalLink>
        </div>
        <div>
          <ExternalLink
            rel="noopener noreferrer"
            target="_blank"
            alt="Spotify"
            href="https://open.spotify.com/user/wklihdpm3qh37fr76e26qekq3?si=C-gMSt7rQL2hdtcXPTLLuQ"
          >
            SPOTIFY
          </ExternalLink>
        </div>
        <div>
          <ExternalLink
            rel="noopener noreferrer"
            target="_blank"
            alt="Facebook"
            href="https://www.facebook.com/marcosweetsandspices"
          >
            FACEBOOK
          </ExternalLink>
        </div>
      </Box>
      <Box sx={styles.hotness}>
        <Text as="p" variant="secondary">
          {"The hottest thing to hit your freezer."}
        </Text>
      </Box>
      <Box sx={styles.copyright}>
        <Text as="p" variant="secondary">
          © {date.getFullYear()} Marco
        </Text>
      </Box>
      <Box sx={styles.credit}>
        <Link
          to="#"
          onClick={e => {
            e.preventDefault()
            toggleSiteCredit()
          }}
          sx={{
            color: "#333",
            opacity: 0.5,
            textDecoration: "underline",
            fontFamily: "secondary",
          }}
        >
          Site Credit
        </Link>
      </Box>
      <SiteCredit
        toggleSiteCredit={toggleSiteCredit}
        isSiteCreditOpen={isSiteCreditOpen}
      />
    </Grid>
  )
}

export default Footer
